import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/request'
import { registerTenantReset as registerTenantResetPure, runTenantReset } from '@/utils/tenantStamp'

interface UserInfo {
  id: string
  username: string
  email: string
  role: string
  tenant_id: string | null
  tenant_name?: string | null
  is_superuser: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserInfo | null>(null)
  const permissions = ref<string[]>([])
  const menuTree = ref<any[]>([])
  const isLoggingOut = ref(false)
  const isInitializing = ref(false)
  
  // 当前选中的入驻企业ID（superadmin可选，普通用户固定为自己的）
  // 持久化到 localStorage，避免刷新后租户状态丢失
  const selectedTenantId = ref<string | null>(localStorage.getItem('selected_tenant_id'))
  // 入驻企业列表（superadmin可见）
  const tenants = ref<any[]>([])

  // 1102-FE4 · 切租户专用的「非负计数器」：每次 selectTenant 自增 1。
  // MainLayout 用它作为 <router-view> 的 :key 强制重挂当前页签
  // （onMounted 取数的页签借此重新走一遍取数流程）；
  // 业务 store/组件也可以 watch 它来触发自己的刷新。不是为了让外部手动改。
  const tenantNonce = ref(0)
  // 1102-FE4 · 「按租户清理」注册表已抽到 utils/tenantStamp.ts（纯模块，无 vue 依赖，
  //   便于脚本直接断言纯逻辑）。此处只做接线：把 store 里同名函数转发出去，
  //   保持组件侧 `useAuthStore().registerTenantReset(...)` 的用法不变。
  function registerTenantReset(handler: () => void): () => void {
    return registerTenantResetPure(handler)
  }
  
  // 初始化时恢复登录状态
  const initPromise: Promise<void> = (async () => {
    const token = localStorage.getItem('access_token')
    if (!token) return
    isInitializing.value = true
    try {
      const res: any = await api.get('/auth/me')
      user.value = res.data
      // 如果是超级管理员，加载入驻企业列表
      if (res.data?.is_superuser) {
        await fetchTenants()
        // 默认选择第一个入驻企业
        if (tenants.value.length > 0) {
          const saved = localStorage.getItem('selected_tenant_id')
          if (saved && tenants.value.some((t: any) => t.id === saved)) {
            selectedTenantId.value = saved
          } else {
            selectedTenantId.value = tenants.value[0].id
            localStorage.setItem('selected_tenant_id', selectedTenantId.value || '')
          }
        }
      } else {
        selectedTenantId.value = res.data?.tenant_id || null
        localStorage.setItem('selected_tenant_id', selectedTenantId.value || '')
      }
      await Promise.all([fetchMyPermissions(), fetchMenuTree()])
    } catch {
      logout()
    } finally {
      isInitializing.value = false
    }
  })()
  
  const isLoggedIn = computed(() => !!user.value)
  const isSuperAdmin = computed(() => user.value?.is_superuser || false)
  const role = computed(() => user.value?.role || '')
  
  // 有效的tenant_id：普通用户使用自己的，superadmin使用选中的
  const effectiveTenantId = computed(() => {
    if (isSuperAdmin.value) {
      return selectedTenantId.value
    }
    return user.value?.tenant_id || null
  })
  
  // 当前入驻企业名称
  const currentTenantName = computed(() => {
    // 1) 平台超管且选择了具体企业：优先从 tenants 列表查名（超管视角）
    if (isSuperAdmin.value && effectiveTenantId.value) {
      const t = tenants.value.find((x: any) => x.id === effectiveTenantId.value)
      if (t?.name) return t.name
    }
    // 2) 普通企业用户：/auth/me 已返回 tenant_name（1101-T7）；超管查不到时同样兜底
    if (user.value?.tenant_name) return user.value.tenant_name
    // 3) 无租户上下文
    if (!effectiveTenantId.value) return '全部入驻企业'
    return '未知入驻企业'
  })

  // 主人 V1.7 需求：商务作业父菜单砍掉，7 个子菜单从侧边栏隐藏
  //   - menu:business 是 menu-tree 中的折叠父菜单（type === 'menu'），整棵砍掉
  //   - 7 个 page 直接隐藏：商务工作区 / 货盘与报价 / 询船管理 / 船况声明 /
  //     合同管理 / 委托管理 / 经纪人管理 —— 这些只能通过 /business-ledger 页内按钮调用
  //   - 商务台账保留作为顶级菜单入口直显页面
  // 容器 lift 名单：旧 menu:business 与新增 menu:business-ledger 两个折叠容器
  // （type === 'menu'，即 permission_tree 里的 [menu] 节点）均走 lift 分支——
  // 把子 page 提升到当前层级 + 父图标透传。后端 5f5904a 把商务台账改成
  // [menu] menu:business-ledger > [page] menu:business-ledger:page 两层后，
  // 该容器也进 HIDDEN 走 lift，商务台账 page 直显顶级（不再渲染成折叠文件夹）。
  // lift 后 page 'menu:business-ledger:page' 的显隐检查同旧 menu:business 下的
  // page 一致：page 自身 code 或子 button 有权限即显示，无需额外例外处理。
  // 7 个子页面的路由依然存在（router/index.ts 加了 hidden:true），只是不渲染在侧边栏
  // 注：menu:cargo-quotation（货盘与报价）页面已随 4ba86498 退役删除；历史货盘报价记录保留只读，
  //     当前报价走 quote_versions。此处不再登记该权限码（裁定 5：入口退役、孤儿权限码清理）。
  const HIDDEN_MENU_CODES = new Set([
    'menu:business',                  // 折叠容器，lift 子项
    'menu:business-ledger',           // 折叠容器，lift 子项（商务台账 page 直显顶级）
    'menu:business-case',             // 商务工作区（只在商务台账按钮调用）
    'menu:inquiry',                   // 询船管理
    'menu:subjects',                  // 船况声明
    'menu:contracts',                 // 合同管理
    'menu:brokerage',                 // 委托管理
    'menu:broker-deals',              // 经纪人管理
    // 1103-RBAC-2：后端新增「运费三阶段结算」权限节点（settlements:create/update），
    // 前端暂无该页面路由 → 隐藏，不进侧边栏（权限码仍可在角色授权页勾选）。
    'menu:settlement-cases:page',     // 运费三阶段结算
  ])

  // 第 1 批交接能力组已给 OPS 等岗位开放航次交接查看，但旧菜单树的
  // “航次列表”仍只认 voyages:view。列表接口本身由服务端按租户与数据范围过滤；
  // 这里仅补上工作台入口，不扩大后端数据或写动作授权。
  function hasPageAccess(node: any): boolean {
    return (node.code && hasPermission(node.code)) ||
      (node.children || []).some((child: any) => hasPermission(child.code || child.perm)) ||
      (node.code === 'menu:voyage:list' && hasPermission('voyages:handover:view'))
  }

  // 用户在菜单树上至少有任意权限的节点才显示
  function visibleMenu(nodes: any[]): any[] {
    const out: any[] = []
    for (const n of nodes) {
      if (n.type === 'button') continue
      // HIDDEN 但 type === 'menu' 的父节点（折叠容器）：递归提升子项（lift），而不是整棵砍掉
      // 例：menu:business 折叠父级 → 把它的子节点提到当前层级（这样 menu:business-ledger 能保留在顶级）
      // 同时把父菜单的 icon 透传给每个提升上来的顶级子项，让商务台账等显示图标
      if (n.type === 'menu' && n.code && HIDDEN_MENU_CODES.has(n.code)) {
        const lifted = visibleMenu(n.children || [])
        if (lifted.length) {
          for (const it of lifted) {
            if (!it.icon && n.icon) it.icon = n.icon
            out.push(it)
          }
        }
        continue
      }
      // HIDDEN 的 page：整棵砍掉（无子菜单可显示）
      if (n.code && HIDDEN_MENU_CODES.has(n.code)) continue
      // page 类型：检查自身code权限，或子节点（button）有权限
      if (n.type === 'page') {
        if (!hasPageAccess(n)) continue
        out.push(n.children ? { ...n, children: visibleMenu(n.children) } : n)
        continue
      }
      // 其它 menu 类型：递归检查子节点
      if (n.type === 'menu') {
        const has = (n.children || []).some((c: any) => visibleMenuNode(c))
        if (!has) continue
        out.push({ ...n, children: visibleMenu(n.children) })
        continue
      }
    }
    return out
  }

  function visibleMenuNode(n: any): boolean {
    // 检查当前节点自身是否有权限
    const nodePerm = n.code || n.perm
    if (nodePerm && hasPermission(nodePerm)) return true
    if (n.type === 'page') return hasPageAccess(n)
    if (n.type === 'menu') return (n.children || []).some((c: any) => visibleMenuNode(c))
    return false
  }
  
  function hasPermission(perm: string): boolean {
    if (isSuperAdmin.value) return true
    return permissions.value.includes(perm)
  }
  
  async function login(username: string, password: string) {
    const res = await api.post('/auth/login', { username, password })
    // res 已经是拦截器处理后的响应对象: {code, message, data}
    const data = res.data
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)
    // Pinia store 返回的对象已解包，直接赋值给 user
    user.value = data.user
    
    // 如果是超级管理员，加载入驻企业列表
    if (data.user?.is_superuser) {
      await fetchTenants()
      // 默认选择第一个入驻企业
      if (tenants.value.length > 0) {
        selectedTenantId.value = tenants.value[0].id
        localStorage.setItem('selected_tenant_id', selectedTenantId.value || '')
      }
    } else {
      // 普通用户直接使用自己的tenant_id
      selectedTenantId.value = data.user?.tenant_id || null
      localStorage.setItem('selected_tenant_id', selectedTenantId.value || '')
    }
    
    isLoggingOut.value = false
    // 登录后自动加载权限和菜单
    await Promise.all([fetchMyPermissions(), fetchMenuTree()])
    return data
  }
  
  async function fetchTenants() {
    try {
      const res = await api.get('/tenants', { params: { page_size: 200 } })
      tenants.value = res.data || []
    } catch { tenants.value = [] }
  }
  
  async function fetchMyPermissions() {
    try {
      const res = await api.get('/permissions/my')
      permissions.value = res.data || []
    } catch { permissions.value = [] }
  }

  async function fetchMenuTree() {
    try {
      const res = await api.get('/permissions/menu-tree')
      menuTree.value = res.data || []
    } catch { menuTree.value = [] }
  }
  
  function logout() {
    isLoggingOut.value = true
    user.value = null
    permissions.value = []
    menuTree.value = []
    tenants.value = []
    selectedTenantId.value = null
    localStorage.removeItem('selected_tenant_id')
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }
  
  // 切换入驻企业（1102-FE4）：
  //   ① 先跑集中式按租户清理表（清各业务 store/组件里的列表与详情缓存，不动登录态/权限/字典）；
  //   ② 再写 selectedTenantId + localStorage；
  //   ③ tenantNonce++ → MainLayout <router-view :key> 重挂当前页签，触发 onMounted 重取数；
  //   ④ 请求层的租户戳（见 utils/request.ts）随后把旧租户的在途响应静默丢弃。
  //   不许用 location.reload()（会丢用户未保存的输入）；不许等所有在途请求回来（阻塞）。
  function selectTenant(tenantId: string) {
    if (tenantId === selectedTenantId.value) return
    runTenantReset()
    selectedTenantId.value = tenantId
    localStorage.setItem('selected_tenant_id', tenantId)
    tenantNonce.value += 1
  }
  
  return {
    user,
    permissions,
    menuTree,
    tenants,
    selectedTenantId,
    effectiveTenantId,
    currentTenantName,
    isLoggingOut,
    isInitializing,
    initPromise,
    isLoggedIn,
    isSuperAdmin,
    role,
    tenantNonce,
    registerTenantReset,
    hasPermission,
    visibleMenu,
    login,
    fetchMyPermissions,
    fetchMenuTree,
    fetchTenants,
    selectTenant,
    logout
  }
})
